import React, { useEffect, useState } from 'react';
import { FaChevronLeft, FaChevronRight, FaTimes } from 'react-icons/fa';
import { QBTestQuestionEto } from '../lib/api/types';

interface TabletQuestionNavProps {
  questions: QBTestQuestionEto[];
  currentIndex: number;
  onQuestionSelect: (index: number) => void;
  onClose?: () => void;
  timedQuestionMode?: boolean;
  completedQuestions?: Set<string>;
}

const TabletQuestionNav: React.FC<TabletQuestionNavProps> = ({
  questions,
  currentIndex,
  onQuestionSelect,
  onClose,
  timedQuestionMode = false,
  completedQuestions = new Set()
}) => {
  const [isVisible, setIsVisible] = useState(true);

  // Keyboard shortcuts support
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't interfere with input fields
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return;
      }

      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          handlePrevious();
          break;
        case 'ArrowRight':
          e.preventDefault();
          handleNext();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex, questions.length]);

  const handlePrevious = () => {
    if (currentIndex > 0) {
      // In TimedQuestion mode, prevent going back to completed questions
      if (timedQuestionMode) {
        const targetQuestion = questions[currentIndex - 1];
        if (targetQuestion && targetQuestion.questionId && completedQuestions.has(targetQuestion.questionId)) {
          return; // Don't allow navigation to completed questions
        }
      }
      onQuestionSelect(currentIndex - 1);
    }
  };

  const handleNext = () => {
    if (currentIndex < questions.length - 1) {
      onQuestionSelect(currentIndex + 1);
    }
  };

  const handleQuestionClick = (index: number) => {
    // In TimedQuestion mode, prevent going back to completed questions
    if (timedQuestionMode && index < currentIndex) {
      const targetQuestion = questions[index];
      if (targetQuestion && targetQuestion.questionId && completedQuestions.has(targetQuestion.questionId)) {
        return; // Don't allow navigation to completed questions
      }
    }
    onQuestionSelect(index);
  };

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-24 left-1/2 -translate-x-1/2 z-30 backdrop-blur-md bg-white/80 dark:bg-gray-800/80 border border-white/20 rounded-2xl shadow-lg p-4 max-w-2xl mx-auto transition-all duration-300">
      <div className="flex items-center gap-4">
        {/* Previous Button */}
        <button
          onClick={handlePrevious}
          className={`p-3 rounded-full backdrop-blur-md bg-white/10 border border-white/20 shadow-sm hover:bg-white/20 transition-all ${
            currentIndex === 0 || (timedQuestionMode && currentIndex > 0 && questions[currentIndex - 1]?.questionId && completedQuestions.has(questions[currentIndex - 1].questionId!))
              ? 'opacity-30 cursor-not-allowed' 
              : 'hover:scale-110 hover:shadow-lg'
          }`}
          disabled={currentIndex === 0 || Boolean(timedQuestionMode && currentIndex > 0 && questions[currentIndex - 1]?.questionId && completedQuestions.has(questions[currentIndex - 1].questionId!))}
          aria-label="Önceki soru"
        >
          <FaChevronLeft className="w-5 h-5 text-gray-600 dark:text-gray-300" />
        </button>

        {/* Question Indicators */}
        <div className="flex gap-2 overflow-x-auto scrollbar-hide px-2" style={{ maxWidth: '400px' }}>
          {questions.map((question, index) => {
            const isCompleted = timedQuestionMode && question.questionId && completedQuestions.has(question.questionId);
            const isDisabled = timedQuestionMode && index < currentIndex && isCompleted;
            
            return (
              <button
                key={index}
                onClick={() => handleQuestionClick(index)}
                disabled={isDisabled || false}
                className={`
                  min-w-[40px] h-10 rounded-lg font-medium transition-all
                  ${index === currentIndex 
                    ? 'bg-blue-500 text-white shadow-lg scale-110' 
                    : isCompleted
                      ? 'bg-green-100 text-green-600 border border-green-300'
                      : isDisabled
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-white/50 hover:bg-white/70 text-gray-700 border border-gray-200'
                  }
                `}
                title={`Soru ${index + 1}`}
              >
                {index + 1}
              </button>
            );
          })}
        </div>

        {/* Next Button */}
        <button
          onClick={handleNext}
          className={`p-3 rounded-full backdrop-blur-md bg-white/10 border border-white/20 shadow-sm hover:bg-white/20 transition-all ${
            currentIndex === questions.length - 1 
              ? 'opacity-30 cursor-not-allowed' 
              : 'hover:scale-110 hover:shadow-lg'
          }`}
          disabled={currentIndex === questions.length - 1}
          aria-label="Sonraki soru"
        >
          <FaChevronRight className="w-5 h-5 text-gray-600 dark:text-gray-300" />
        </button>

        {/* Close Button */}
        {onClose && (
          <button
            onClick={onClose}
            className="p-2 rounded-full backdrop-blur-md bg-white/10 border border-white/20 shadow-sm hover:bg-white/20 transition-all hover:scale-110 ml-2"
            aria-label="Navigasyonu kapat"
          >
            <FaTimes className="w-4 h-4 text-gray-600 dark:text-gray-300" />
          </button>
        )}
      </div>

      {/* Progress Info */}
      <div className="text-center mt-2 text-sm text-gray-600 dark:text-gray-400">
        Soru {currentIndex + 1} / {questions.length}
      </div>
    </div>
  );
};

export default TabletQuestionNav;